"use client";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { pushEvent } from "@/lib/analytics/gtm";
import { analyticsEvents } from "@/lib/analytics/events";

const ERROR_CAP_PER_SESSION = 20;

export function ErrorTracker() {
  const pathname = usePathname();
  const count = useRef(0);

  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      if (count.current >= ERROR_CAP_PER_SESSION) return;
      count.current += 1;
      pushEvent(analyticsEvents.js_error, {
        name: e.error?.name ?? "Error",
        message: (e.message ?? "").split("\n")[0]?.slice(0, 200),
        path: pathname ?? location.pathname,
        source: "error",
      });
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      if (count.current >= ERROR_CAP_PER_SESSION) return;
      count.current += 1;
      const reason = e.reason as { name?: string; message?: string } | string | undefined;
      const message =
        typeof reason === "string"
          ? reason
          : (reason?.message ?? String(reason ?? "unhandledrejection"));
      pushEvent(analyticsEvents.js_error, {
        name:
          typeof reason === "object"
            ? (reason?.name ?? "UnhandledRejection")
            : "UnhandledRejection",
        message: message.split("\n")[0]?.slice(0, 200),
        path: pathname ?? location.pathname,
        source: "rejection",
      });
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, [pathname]);

  return null;
}
